import { isNodeDevEnv } from "../commons/VersionUtil";

const AUTH_STORAGE_KEY_TOKEN = "__session_token";
const AUTH_STORAGE_KEY_REFRESH = "__session_refresh_token";
const AUTH_CHANNEL = "engagebay-auth";

/** Clear token keys from storage only (no broadcast). Used by clearSession and by other tabs on logout message. */
function clearStorageOnly(): void {
    localStorage.removeItem(AUTH_STORAGE_KEY_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEY_REFRESH);
}

export const setSessionToken = (token: string) => {
    localStorage.setItem(AUTH_STORAGE_KEY_TOKEN, token);
};

export const getSessionToken = (): string | null => {
    return localStorage.getItem(AUTH_STORAGE_KEY_TOKEN);
};

export const setSessionRefreshToken = (token: string) => {
    localStorage.setItem(AUTH_STORAGE_KEY_REFRESH, token);
};

export const getSessionRefreshToken = (): string | null => {
    return localStorage.getItem(AUTH_STORAGE_KEY_REFRESH);
};

/** Clear tokens and broadcast logout so other tabs clear and reload. */
export const clearSession = () => {
    clearStorageOnly();
    if (typeof BroadcastChannel !== "undefined") {
        try {
            const channel = new BroadcastChannel(AUTH_CHANNEL);
            channel.postMessage({ type: "logout" });
            channel.close();
        } catch {
            // Ignore if channel fails (e.g. private mode)
        }
    }
};

/**
 * Navigates to the server-side logout endpoint, which clears all auth cookies
 * (eb_access_token, eb_mfa_token) and redirects to the correct login URL for the
 * tenant (handles standard subdomain, reseller CNAME, and QA/prod versioning).
 *
 * Why this exists: all 401/session-expired paths in the panel previously used a
 * relative '/login' which resolves to the current subdomain instead of the login page.
 * Centralising here avoids duplication and keeps the dev-vs-prod branch in one place.
 *
 * Return-path: when intentional=false (session expiry, 401, forced logout), the current
 * hash fragment is encoded as ?return= so the user is restored to their previous location
 * after re-authentication. When intentional=true (user clicked Logout), the ?return= param
 * is omitted — the user explicitly left, so sending them back to a stale deep-link (e.g.
 * #/contacts/123 that no longer exists or belongs to a different account) causes confusing
 * "not found" errors.
 *
 * Exposed on window as `ebLogout` so legacy Backbone/jQuery code can call it without
 * duplicating the dev/prod branch or the return-path encoding.
 */
export function redirectToLogout(intentional = false): void {
    const hash = typeof window !== "undefined" ? window.location.hash : '';
    const returnParam = (!intentional && hash) ? `?return=${encodeURIComponent(hash)}` : '';
    window.location.href = isNodeDevEnv() ? `/login${returnParam}` : `/logout.jsp${returnParam}`;
}

// Expose on window so legacy JS (nav-util.js, global_error_handler.js, lc-sync.js, etc.)
// can call ebLogout() without duplicating redirect logic or importing ES modules.
if (typeof window !== "undefined") {
    (window as any).clearSession = clearSession;
    (window as any).ebLogout = redirectToLogout;
}

// Cross-tab listener: when another tab logs out, clear this tab's storage and reload
if (typeof BroadcastChannel !== "undefined") {
    try {
        const channel = new BroadcastChannel(AUTH_CHANNEL);
        channel.onmessage = (e: MessageEvent<{ type?: string }>) => {
            if (e.data?.type === "logout") {
                clearStorageOnly();
                // Another tab intentionally logged out — don't restore this tab's deep-link
                redirectToLogout(true);
            }
        };
    } catch {
        // BroadcastChannel not available (e.g. old browser)
    }
}
